// src/App.js
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ToastProvider } from './components/ui/Toast';
import { Spinner } from './components/ui';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GlobalFiltersProvider } from './context/GlobalFiltersContext';
import { PostHogProvider } from './context/PostHogContext';
import usePageTitle from './hooks/usePageTitle';

// Auth pages — kept eager (small, needed immediately)
import Login from './pages/Login';
import Register from './pages/Register';
import NotFound from './pages/NotFound';

// Lazy-loaded pages
const Organizations = lazy(() => import('./pages/Organizations'));
const NoOrganization = lazy(() => import('./pages/NoOrganization'));
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'));
const GoogleOAuthStart = lazy(() => import('./pages/GoogleOAuthStart'));
const OAuthDebug = lazy(() => import('./pages/OAuthDebug'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const InvitationAccept = lazy(() => import('./pages/InvitationAccept'));
const DemoDashboard = lazy(() => import('./pages/DemoDashboard'));
const RevolutOAuthCallback = lazy(() => import('./components/RevolutOAuthCallback'));

// Lazy-loaded content pages (heavy)
const Layout = lazy(() => import('./Layout'));
const Overview = lazy(() => import('./pages/Overview'));
const TransactionsList = lazy(() => import('./pages/TransactionsList'));
const TagsManagement = lazy(() => import('./pages/TagsManagement'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const TagDrillDownAnalytics = lazy(() => import('./pages/TagDrillDownAnalytics'));
const DataSources = lazy(() => import('./pages/DataSources'));
const TaggingRules = lazy(() => import('./pages/TaggingRules'));
const DCFValuation = lazy(() => import('./pages/DCFValuation'));
const RevenueForecast = lazy(() => import('./pages/RevenueForecast'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const OrganizationEdit = lazy(() => import('./pages/OrganizationEdit'));
const OrganizationUserManagement = lazy(() => import('./pages/OrganizationUserManagement'));
const AIChat = lazy(() => import('./pages/AIChat'));
const AddDataSource = lazy(() => import('./pages/AddDataSource'));
const CreateTaggingRule = lazy(() => import('./pages/CreateTaggingRule'));
const MetricsPage = lazy(() => import('./pages/MetricsPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const IncomeExpenseFlow = lazy(() => import('./pages/IncomeExpenseFlow'));
const EditDataSource = lazy(() => import('./pages/EditDataSource'));
const SetupProgress = lazy(() => import('./pages/SetupProgress'));
const AICategorize = lazy(() => import('./pages/AICategorize'));
const Charts = lazy(() => import('./pages/Charts'));
const Reports = lazy(() => import('./pages/Reports'));
const ReportDetail = lazy(() => import('./pages/ReportDetail'));

// Suspense fallback
const PageLoader = () => (
    <div className="flex items-center justify-center h-screen">
        <Spinner size="lg" />
    </div>
);

// Wrapper component to enforce organization selection
const OrganizationRequiredRoute = ({ children }) => {
    const { currentOrganization, isLoading } = useAuth();

    if (isLoading) {
        return <PageLoader />;
    }

    if (!currentOrganization) {
        return <Navigate to="/no-organization" replace />;
    }

    return children;
};

// Shared layout route — stays mounted across page navigations.
// Only the <Outlet /> (page content) swaps, preventing sidebar/header flicker.
const ProtectedLayout = () => {
    const { currentOrganization } = useAuth();
    return (
        <PrivateRoute>
            <OrganizationRequiredRoute>
                <GlobalFiltersProvider>
                    <Layout>
                        <ErrorBoundary>
                            <div key={currentOrganization?.id}>
                                <Suspense fallback={
                                    <div className="flex items-center justify-center h-64">
                                        <Spinner size="lg" />
                                    </div>
                                }>
                                    <Outlet />
                                </Suspense>
                            </div>
                        </ErrorBoundary>
                    </Layout>
                </GlobalFiltersProvider>
            </OrganizationRequiredRoute>
        </PrivateRoute>
    );
};

const PageTitleUpdater = () => {
    usePageTitle();
    return null;
};

function App() {
    return (
        <ToastProvider>
            <Router>
                <PageTitleUpdater />
                <PostHogProvider>
                    <AuthProvider>
                        <ErrorBoundary>
                            <Suspense fallback={<PageLoader />}>
                                <Routes>
                                    {/* Public routes */}
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/register" element={<Register />} />
                                    <Route path="/invitation/:token" element={<InvitationAccept />} />
                                    <Route path="/demo-dash" element={<DemoDashboard />} />
                                    <Route path="/oauth-debug" element={<OAuthDebug />} />

                                    {/* OAuth callback routes (public) */}
                                    <Route path="/auth/google/start" element={<GoogleOAuthStart />} />
                                    <Route path="/auth/callback/google" element={<GoogleCallback />} />
                                    <Route path="/onboarding" element={<Onboarding />} />

                                    {/* Organization selection routes */}
                                    <Route path="/organizations" element={
                                        <PrivateRoute><Organizations /></PrivateRoute>
                                    } />
                                    <Route path="/organizations/:id/edit" element={
                                        <PrivateRoute><OrganizationEdit /></PrivateRoute>
                                    } />
                                    <Route path="/organizations/:id/users" element={
                                        <PrivateRoute><OrganizationUserManagement /></PrivateRoute>
                                    } />
                                    <Route path="/no-organization" element={
                                        <PrivateRoute><NoOrganization /></PrivateRoute>
                                    } />

                                    {/* OAuth callback routes */}
                                    <Route path="/data-sources-callback/revolut" element={
                                        <PrivateRoute><RevolutOAuthCallback /></PrivateRoute>
                                    } />

                                    {/* Protected routes — Layout stays mounted, only page content swaps */}
                                    <Route element={<ProtectedLayout />}>
                                        <Route path="/" element={<Overview />} />
                                        <Route path="/transactions" element={<TransactionsList />} />
                                        <Route path="/tags" element={<TagsManagement />} />
                                        <Route path="/tagging-rules" element={<TaggingRules />} />
                                        <Route path="/tagging-rules/new" element={<CreateTaggingRule />} />
                                        <Route path="/tagging-rules/:id/edit" element={<CreateTaggingRule />} />
                                        <Route path="/charts" element={<Charts />} />
                                        <Route path="/analytics" element={<AnalyticsDashboard />} />
                                        <Route path="/analytics/tag-drill-down" element={<TagDrillDownAnalytics />} />
                                        <Route path="/analytics/income-expense-flow" element={<IncomeExpenseFlow />} />
                                        <Route path="/metrics" element={<MetricsPage />} />
                                        <Route path="/customers" element={<CustomersPage />} />
                                        <Route path="/valuation/dcf" element={<DCFValuation />} />
                                        <Route path="/forecasts/revenue" element={<RevenueForecast />} />
                                        <Route path="/data-sources" element={<DataSources />} />
                                        <Route path="/data-sources/add" element={<AddDataSource />} />
                                        <Route path="/data-sources/:id/edit" element={<EditDataSource />} />
                                        <Route path="/ai-chat" element={<AIChat />} />
                                        <Route path="/setup" element={<SetupProgress />} />
                                        <Route path="/ai-categorize" element={<AICategorize />} />
                                        <Route path="/user-management" element={<UserManagement />} />
                                        <Route path="/reports" element={<Reports />} />
                                        <Route path="/reports/:id" element={<ReportDetail />} />
                                    </Route>

                                    {/* 404 page for unknown routes */}
                                    <Route path="*" element={<NotFound />} />
                                </Routes>
                            </Suspense>
                        </ErrorBoundary>
                    </AuthProvider>
                </PostHogProvider>
            </Router>
        </ToastProvider>
    );
}

export default App;
